import { Check } from "lucide-react";
import { OptionItemProps } from "../types";
import { areAllChildrenSelected, getLeftPaddingSize } from "../utils";
import { cn } from "../../../utils";
import { CommandItem } from "../../../ui/command";
import { isEmpty } from "lodash-es";
import { DIR_DIRECTION } from "../consts";
import { Checkbox } from "../../../ui/checkbox";
import { Badge } from "../../../ui/badge";

/**
 * Recursively renders option items with support for nested children
 */
export function OptionItem({
  option,
  selected,
  onSelect,
  nestLevel = 0,
  disabled = false,
  dir = DIR_DIRECTION.LTR,
  selectionIndicator = "checkmark",
  size = "default",
  optionVariant = "default",
}: OptionItemProps & { parentSelected?: boolean }): JSX.Element {
  const isStacked = optionVariant === "stacked";
  const isParent = !isEmpty(option.children);
  const isSelected = selected.has(option.value);
  const optionPaddingLeft = `${getLeftPaddingSize(nestLevel)}px`;

  // Determine selection states for parents
  const allChildrenSelected = areAllChildrenSelected(option, selected);

  // Show check only if: leaf is selected OR all children are selected
  const shouldShowCheck = isSelected || allChildrenSelected;
  // Emphasize only when this item is selected and no ancestor is selected

  const renderIconIfNeeded = () => {
    if (option.icon) {
      return (
        <div className="shrink-0 [&>svg]:text-text-interactive-neutral-idle!">
          {option.icon}
        </div>
      );
    }
    return undefined;
  };

  const renderOptionSelectedIcon = () => {
    if (selectionIndicator === "none") {
      return <></>;
    }

    if (selectionIndicator === "checkbox") {
      const checkboxState = option.showIndeterminateCheckbox
        ? "indeterminate"
        : shouldShowCheck;

      return <Checkbox checked={checkboxState} />;
    }

    return (
      <Check
        className={cn(
          "size-4 shrink-0 transition-opacity text-text-interactive-neutral-idle opacity-0",
          { "opacity-100": shouldShowCheck },
        )}
      />
    );
  };

  const renderChildrenIfNeeded = () => {
    if (isParent) {
      const shouldDisableChildren =
        !!option.disableChildrenOnParentSelect && isSelected;

      return option.children!.map(child => (
        <OptionItem
          key={child.value}
          option={child}
          selected={selected}
          onSelect={onSelect}
          nestLevel={nestLevel + 1}
          dir={dir}
          selectionIndicator={selectionIndicator}
          size={size}
          optionVariant={optionVariant}
          parentSelected={shouldShowCheck}
          disabled={shouldDisableChildren || disabled || child.disabled}
        />
      ));
    }
    return undefined;
  };

  const renderLabelBlock = () => {
    if (isStacked) {
      return (
        <div className="flex-1 flex flex-col min-w-0">
          <span className="whitespace-nowrap text-text-interactive-neutral-idle hover:text-text-interactive-neutral-hover">
            {option.label}
          </span>
          {option.description && (
            <span className="whitespace-nowrap text-xs text-text-interactive-neutral-disabled">
              {option.description}
            </span>
          )}
        </div>
      );
    }

    return (
      <span className="flex-1 flex items-center gap-2 whitespace-nowrap text-text-interactive-neutral-active">
        {option.label}
        {option.description && <Badge>{option.description}</Badge>}
      </span>
    );
  };

  const renderCommandItemContent = () => {
    return (
      <div
        className={cn("flex w-full gap-3 flex-row", {
          "flex-row-reverse": dir === DIR_DIRECTION.LTR,
          "items-center": !isStacked,
          "items-start": isStacked,
        })}
        style={{ paddingLeft: optionPaddingLeft }}
      >
        <div
          className={cn("flex gap-2 flex-1", {
            "items-center": !isStacked,
            "items-start": isStacked,
          })}
        >
          {renderIconIfNeeded()}
          {renderLabelBlock()}
        </div>

        {renderOptionSelectedIcon()}
      </div>
    );
  };

  return (
    <>
      <CommandItem
        value={option.value}
        disabled={option.disabled || disabled}
        onSelect={() => onSelect(option.value, isParent)}
        className={cn(
          "text-text-interactive-neutral-idle hover:text-text-interactive-neutral-hover! hover:bg-fill-interactive-neutral-hover! focus:text-text-interactive-neutral-active",
          "focus:bg-fill-interactive-neutral-hover! data-[selected=true]:text-text-interactive-neutral-hover! data-[selected=true]:bg-fill-interactive-neutral-hover!",
          "cursor-pointer disabled:cursor-not-allowed rounded-[2px]",
          {
            "px-2 py-1.5 text-xs": size === "xs",
            "px-3 py-1.5": size === "default",
            "bg-fill-interactive-neutral-idle": shouldShowCheck,
          },
        )}
      >
        {renderCommandItemContent()}
      </CommandItem>
      {renderChildrenIfNeeded()}
    </>
  );
}
